import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * TEMU 门禁深色下拉：避开原生 select 白底（与 SHEIN SearchableSelect 观感对齐）。
 * searchable=true 时为可输入过滤的 combobox（店铺选择用）。
 * 可搜索模式默认禁止浏览器账号密码快速填充，仅允许键入模糊查询或点选。
 * @param {{
 *   value: string,
 *   options: Array<{ value: string, label: string }>,
 *   onChange: (value: string) => void,
 *   placeholder?: string,
 *   disabled?: boolean,
 *   emptyText?: string,
 *   searchable?: boolean,
 *   allowClear?: boolean,
 *   inputName?: string,
 * }} props
 */
export default function TemuGateSelect({
  value,
  options = [],
  onChange,
  placeholder = fixedUi("请选择"),
  disabled = false,
  emptyText = fixedUi('暂无选项'),
  searchable = false,
  allowClear = false,
  inputName,
}) {
  const listId = useId();
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const blockAutofillRef = useRef(searchable);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  // searchable：保持只读直到用户真正按键/粘贴，避免密码管理器在 focus 时写入
  const [blockAutofill, setBlockAutofill] = useState(searchable);

  useEffect(() => {
    blockAutofillRef.current = blockAutofill;
  }, [blockAutofill]);

  const selected = useMemo(
    () => options.find((opt) => String(opt.value) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    if (!searchable) return options;
    const q = keyword.trim().toLowerCase();
    if (!q) return options;
    return options.filter((opt) => String(opt.label || '').toLowerCase().includes(q));
  }, [options, keyword, searchable]);

  const unlockTyping = () => {
    if (inputRef.current) inputRef.current.readOnly = false;
    blockAutofillRef.current = false;
    setBlockAutofill(false);
  };

  const lockTyping = () => {
    if (!searchable) return;
    if (inputRef.current) inputRef.current.readOnly = true;
    blockAutofillRef.current = true;
    setBlockAutofill(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setKeyword('');
        lockTyping();
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setKeyword('');
        lockTyping();
      }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, searchable]);

  useEffect(() => {
    if (disabled) {
      setOpen(false);
      setKeyword('');
      lockTyping();
    }
  }, [disabled]);

  const pick = (nextValue) => {
    onChange?.(nextValue);
    setOpen(false);
    setKeyword('');
    lockTyping();
  };

  const openPanel = ({ unlockForTyping = false } = {}) => {
    if (disabled) return;
    if (searchable && unlockForTyping) unlockTyping();
    setOpen(true);
    setKeyword('');
  };

  const focusInput = () => {
    const el = inputRef.current;
    if (!el || disabled) return;
    el.focus();
    try {
      const len = el.value?.length ?? 0;
      el.setSelectionRange(len, len);
    } catch {
      /* ignore */
    }
  };

  const displayValue = searchable && open ? keyword : (selected?.label || '');

  return (
    <div
      className={`temu-gate-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${searchable ? ' is-searchable' : ''}`}
      ref={rootRef}
    >
      {searchable ? (
        <div
          className="temu-gate-select__control"
          onMouseDown={(e) => {
            if (disabled) return;
            if (e.button !== 0) return;
            if (e.target?.closest?.('.temu-gate-select__clear')) return;
            openPanel({ unlockForTyping: true });
            if (e.target !== inputRef.current) {
              e.preventDefault();
              focusInput();
            }
          }}
        >
          <input
            ref={inputRef}
            type="search"
            name={inputName ? `${inputName}-query` : 'temu-gate-shop-query'}
            className="temu-gate-select__input"
            value={displayValue}
            disabled={disabled}
            placeholder={selected ? selected.label : placeholder}
            autoComplete="one-time-code"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            inputMode="search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls={listId}
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
            data-form-type="other"
            data-autofill="off"
            readOnly={blockAutofill}
            onFocus={() => openPanel({ unlockForTyping: true })}
            onBlur={lockTyping}
            onChange={(e) => {
              if (blockAutofillRef.current) return;
              setKeyword(e.target.value);
              setOpen(true);
            }}
            onPaste={(e) => {
              if (!blockAutofillRef.current) return;
              e.preventDefault();
              const text = e.clipboardData?.getData('text') || '';
              unlockTyping();
              setKeyword(text);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (blockAutofillRef.current) {
                if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
                  e.preventDefault();
                  unlockTyping();
                  setKeyword(e.key);
                  setOpen(true);
                  return;
                }
                if (e.key === 'Backspace' || e.key === 'Delete') {
                  e.preventDefault();
                  unlockTyping();
                  setKeyword('');
                  setOpen(true);
                  return;
                }
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                const first = filtered[0];
                if (first) pick(first.value);
              }
              if (e.key === 'Escape') {
                setOpen(false);
                setKeyword('');
                lockTyping();
              }
              if (e.key === 'Backspace' && !keyword && allowClear && value) {
                onChange?.('');
              }
            }}
          />
          {allowClear && value && !disabled ? (
            <button
              type="button"
              className="temu-gate-select__clear"
              aria-label={fixedUi("清除")}
              onClick={() => {
                onChange?.('');
                setKeyword('');
                lockTyping();
                focusInput();
              }}
            >
              ×
            </button>
          ) : (
            <ChevronDown size={16} className="temu-gate-select__caret" aria-hidden="true" />
          )}
        </div>
      ) : (
        <button
          type="button"
          className="temu-gate-select__trigger"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => {
            if (!disabled) setOpen((v) => !v);
          }}
        >
          <span className={`temu-gate-select__value${selected ? '' : ' is-placeholder'}`}>
            {selected?.label || placeholder}
          </span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      )}
      {open ? (
        <ul id={listId} className="temu-gate-select__menu" role="listbox">
          {filtered.length === 0 ? (
            <li className="temu-gate-select__empty">{emptyText}</li>
          ) : (
            filtered.map((opt) => {
              const active = String(opt.value) === String(value);
              return (
                <li key={String(opt.value)} role="option" aria-selected={active}>
                  <button
                    type="button"
                    className={`temu-gate-select__option${active ? ' is-active' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(opt.value)}
                  >
                    {opt.label}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
